import { useState } from 'react';
import { Mail, MailX, ArrowRight, Users } from 'lucide-react';
import { ReportRecipientsEditor } from './components/ReportRecipientsEditor';
import type { SiteReportRecipients } from '../../services/sitesService';
import { track } from '../../services/telemetry';
import { rememberRecipientsOk } from './recipientsPrompt';

/**
 * First-inspection recipients check (Jim 10/21): before the first app
 * inspection of a site, show who the report will be emailed to, with a way
 * to the contact list. One tap confirms; the site's next inspections never
 * ask again.
 */
export function RecipientsGate({
  siteId,
  organizationId,
  data,
  onContinue,
}: {
  siteId: string;
  organizationId?: string | null;
  data: SiteReportRecipients;
  onContinue: () => void;
}) {
  const nobody = data.recipients.length === 0;
  // JIM-1027-13: the contact tool opens here, in place; the list refetches
  // when it closes.
  const [editing, setEditing] = useState(false);
  const confirm = () => {
    rememberRecipientsOk(siteId);
    track('recipients_gate_confirmed', {
      siteId,
      recipients: data.recipients.length,
    });
    onContinue();
  };
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 px-4 py-6">
      <div className="max-w-md mx-auto bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center flex-shrink-0">
            <Users className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Who gets this report?
            </h1>
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
              {data.site_name} · first inspection in the app
            </p>
          </div>
        </div>

        {nobody ? (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 px-3 py-2.5 text-sm text-amber-800 dark:text-amber-200 mb-4"
          >
            <MailX className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              Nobody is set to receive this site's reports. Add a contact or
              switch their email on before you send.
            </span>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
              The report will be emailed to:
            </p>
            <ul className="divide-y divide-slate-100 dark:divide-slate-700 rounded-xl border border-slate-200 dark:border-slate-700 mb-4">
              {data.recipients.map((r) => (
                <li key={r.email} className="px-3 py-2">
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5 text-green-600 dark:text-green-400 shrink-0" />
                    <span className="truncate">{r.name}</span>
                    {r.title && (
                      <span className="text-xs font-normal text-slate-500 dark:text-slate-400 truncate">
                        · {r.title}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate pl-5">
                    {r.email}
                  </p>
                </li>
              ))}
            </ul>
          </>
        )}

        {!data.live && !nobody && (
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
            Until this site is marked live, everyone on its contact list gets
            the report, switched on or not.
          </p>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => {
              track('recipients_gate_edit', { siteId });
              setEditing(true);
            }}
            className="flex-1 py-3 rounded-xl border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-medium text-sm text-center hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            Edit contacts
          </button>
          <button
            type="button"
            onClick={confirm}
            className="flex-1 py-3 rounded-xl bg-primary-600 text-white font-medium text-sm hover:bg-primary-700 transition-colors flex items-center justify-center gap-2"
          >
            {nobody ? 'Continue anyway' : 'Looks right'}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      {editing && (
        <ReportRecipientsEditor
          siteId={siteId}
          organizationId={organizationId}
          onClose={() => setEditing(false)}
        />
      )}
    </div>
  );
}
